// Mini-i18n for the widget. Stays a flat key→string map so the JSON bundles
// gzip well and the runtime stays tiny (no plurals lib, no date formatting —
// `formatRelativeTime` already handles that). DE and EN are kept at strict
// parity (every key in both files); the harness still falls back to DE if a
// key ever goes missing in EN.
import de from './locales/de.json';
import en from './locales/en.json';

type Messages = typeof de;
type PartialMessages = Partial<Messages>;

const messages: Record<Locale, PartialMessages> = {
  de: de as PartialMessages,
  en: en as PartialMessages,
};
export type Locale = 'de' | 'en';

const SUPPORTED: readonly Locale[] = ['de', 'en'];
const DEFAULT: Locale = 'en';

let activeLocale: Locale = DEFAULT;

export function setLocale(l: Locale): void {
  activeLocale = l;
}

export function getLocale(): Locale {
  return activeLocale;
}

export function isLocale(value: unknown): value is Locale {
  return value === 'de' || value === 'en';
}

/**
 * Picks a locale from a free-form language tag — accepts BCP-47 (`de-CH`,
 * `en-US`) and bare codes. Matches by primary subtag only. Returns null when
 * nothing matches, letting the caller decide whether to fall back.
 */
export function pickLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED.find((l) => l === primary) ?? null;
}

/**
 * Auto-detects from browser context (URL param > script attribute > navigator).
 * Used at widget boot — no auth context yet. Returns DEFAULT if nothing hits.
 */
export function detectLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get('pinmerge_locale');
  const fromUrlPicked = pickLocale(fromUrl);
  if (fromUrlPicked) return fromUrlPicked;

  const tag = document.currentScript?.getAttribute('data-locale');
  const fromAttr = pickLocale(tag);
  if (fromAttr) return fromAttr;

  const fromNav = pickLocale(navigator.language);
  if (fromNav) return fromNav;

  return DEFAULT;
}

export function t(key: keyof Messages, vars?: Record<string, string | number>): string {
  const raw = messages[activeLocale][key] ?? de[key] ?? String(key);
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) =>
    vars[k] !== undefined ? String(vars[k]) : '',
  );
}

// Pick `keyOne` when count === 1, else `keyOther`. Variables get the `count`
// injected automatically alongside whatever caller-side context is provided.
export function tPlural(
  keyOne: keyof Messages,
  keyOther: keyof Messages,
  count: number,
  vars?: Record<string, string | number>,
): string {
  const k = count === 1 ? keyOne : keyOther;
  return t(k, { count, ...(vars ?? {}) });
}
